import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, Star } from "lucide-react";
import { api } from "@/lib/api";

// Map item_id -> {emoji, slot, style}
export const ACCESSORY_VISUALS = {
    acc_wizard_hat: { emoji: "🎩", slot: "head", style: { top: "-10%", left: "50%", transform: "translateX(-50%)", fontSize: "60px" } },
    acc_witch_hat: { emoji: "🧙", slot: "head", style: { top: "-12%", left: "50%", transform: "translateX(-50%)", fontSize: "62px" } },
    acc_moon_crown: { emoji: "👑", slot: "head", style: { top: "-8%", left: "50%", transform: "translateX(-50%)", fontSize: "56px" } },
    acc_halo: { emoji: "😇", slot: "head", style: { top: "-14%", left: "50%", transform: "translateX(-50%)", fontSize: "44px", filter: "drop-shadow(0 0 20px rgba(255,209,102,0.6))" } },
    acc_star_collar: { emoji: "⭐", slot: "neck", style: { top: "62%", left: "50%", transform: "translateX(-50%)", fontSize: "32px" } },
    acc_moon_pendant: { emoji: "🌙", slot: "neck", style: { top: "62%", left: "50%", transform: "translateX(-50%)", fontSize: "32px" } },
    acc_bowtie: { emoji: "🎀", slot: "neck", style: { top: "60%", left: "50%", transform: "translateX(-50%)", fontSize: "30px" } },
    acc_wings: { emoji: "🪽", slot: "back", style: { top: "30%", left: "0%", fontSize: "70px", transform: "scaleX(-1)" } },
    acc_sparkle_aura: { emoji: "✨", slot: "aura", style: { top: "-5%", right: "-5%", fontSize: "36px" } },
    acc_carpet: { emoji: "🧞", slot: "mount", style: { bottom: "-12%", left: "50%", transform: "translateX(-50%)", fontSize: "70px" } },
    acc_cauldron: { emoji: "🍯", slot: "companion", style: { bottom: "0%", right: "-12%", fontSize: "44px" } },
    acc_yarnball: { emoji: "🧶", slot: "companion", style: { bottom: "0%", right: "-14%", fontSize: "40px" } },
    acc_mouse_toy: { emoji: "🐭", slot: "companion", style: { bottom: "5%", right: "-12%", fontSize: "36px" } },
    acc_feather: { emoji: "🪶", slot: "companion", style: { top: "10%", right: "-15%", fontSize: "40px" } },
    acc_wand: { emoji: "🪄", slot: "hand", style: { top: "45%", right: "-18%", fontSize: "44px", transform: "rotate(-25deg)" } },
    acc_staff: {emoji: "🪄", slot: "staff", style: { top: "45%", left: "-18%", fontSize: "48px", transform: "rotate(20deg)",}},
    acc_tail_shadow: { emoji: "🦊", slot: "tail", style: { bottom: "8%", left: "-10%", fontSize: "42px",}},
    acc_ears_bat: { emoji: "🦇", slot: "ears", style: { top: "-6%", left: "50%", transform: "translateX(-50%)", fontSize: "44px", }},
    acc_markings_moon: { emoji: "🌙", slot: "markings", style: { top: "38%", left: "52%", fontSize: "26px",}},
};

// Customization item_id -> value lookup
const CUSTOM_LOOKUP = {
    fur_blue: { kind: "fur", value: "#3a86ff" },
    fur_violet: { kind: "fur", value: "#b388ff" },
    fur_rose: { kind: "fur", value: "#ff6fa1" },
    fur_emerald: { kind: "fur", value: "#4ade80" },
    fur_gold: { kind: "fur", value: "#ffd166" },
    fur_shadow: { kind: "fur", value: "#222336" },
    eye_round: { kind: "eye_shape", value: "round" },
    eye_almond: { kind: "eye_shape", value: "almond" },
    eye_sleepy: { kind: "eye_shape", value: "sleepy" },
    eyec_dark: { kind: "eye_color", value: "#0a0e1a" },
    eyec_gold: { kind: "eye_color", value: "#ffd166" },
    eyec_emerald: { kind: "eye_color", value: "#4ade80" },
    eyec_violet: { kind: "eye_color", value: "#b388ff" },
    mouth_smile: { kind: "mouth", value: "smile" },
    mouth_grin: { kind: "mouth", value: "grin" },
    mouth_smirk: { kind: "mouth", value: "smirk" },
};

function lighten(hex, amt) {
    const c = hex.replace("#", "");
    const n = parseInt(c, 16);
    const r = Math.min(255, Math.max(0, ((n >> 16) & 255) + amt));
    const g = Math.min(255, Math.max(0, ((n >> 8) & 255) + amt));
    const b = Math.min(255, Math.max(0, (n & 255) + amt));
    return `rgb(${r}, ${g}, ${b})`;
}

export const Blueberry = ({
    level = 4,
    size = 240,
    equipped = {},
    positions = {},
    onPositionChange,
    draggable = false,
    celebrateClass = "",
    expression = "neutral", // 'happy' | 'sad' | 'tear' | 'hurt' | 'sleeping' | 'neutral'
}) => {
    // Fetch custom items so we can render data_url overlays for cst_* equipped items.
    const [customMap, setCustomMap] = useState({});
    const containerRef = React.useRef(null);
    const [dragSlot, setDragSlot] = useState(null);
    const [localPos, setLocalPos] = useState({}); // live position during drag

    useEffect(() => {
        const ids = Object.values(equipped || {}).filter(
            (v) => typeof v === "string" && v.startsWith("cst_")
        );
        if (ids.length === 0) return;
        const need = ids.filter((id) => !customMap[id]);
        if (need.length === 0) return;
        api.get("/custom-items").then((r) => {
            const m = {};
            (r.data || []).forEach((it) => {
                m[it.item_id] = it;
            });
            setCustomMap(m);
        }).catch(() => {});
    }, [equipped]); // eslint-disable-line react-hooks/exhaustive-deps

    const beginDrag = (slot, e) => {
        if (!draggable) return;
        e.preventDefault();
        e.stopPropagation();
        setDragSlot(slot);
        try { e.target.setPointerCapture?.(e.pointerId); } catch {}
    };

    const onDragMove = (slot, e) => {
        if (dragSlot !== slot || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setLocalPos((p) => ({ ...p, [slot]: { x, y } }));
    };

    const endDrag = (slot) => {
        if (dragSlot !== slot) return;
        const pos = localPos[slot];
        setDragSlot(null);
        if (pos && onPositionChange) {
            onPositionChange(slot, pos.x, pos.y);
        }
    };
    const opacity = level === 4 ? 1 : level === 3 ? 0.85 : level === 2 ? 0.45 : 0.35;
    const baseAnim =
        level === 4
            ? "bb-anim-bouncy bb-glow-strong"
            : level === 3
              ? "bb-anim-droop bb-glow-dim"
              : level === 2
                ? "bb-anim-distant"
                : "bb-anim-spirit";
    const animClass = celebrateClass || baseAnim;

    const fur = CUSTOM_LOOKUP[equipped?.fur_color]?.value || "#3a86ff";
    const eyeShape = CUSTOM_LOOKUP[equipped?.eye_shape]?.value || "round";
    const eyeColor = CUSTOM_LOOKUP[equipped?.eye_color]?.value || "#0a0e1a";
    const mouthKind = CUSTOM_LOOKUP[equipped?.mouth]?.value || "smile";

    const gradId = useMemo(() => "bbFur_" + Math.random().toString(36).slice(2, 8), []);
    const stroke = level === 1 ? lighten(fur, 30) : "rgba(124,193,255,0.4)";
    const strokeWidth = level === 1 ? 1.4 : 0;
    const fillFur = level === 1 ? "none" : `url(#${gradId})`;

    // Eye Y radius depending on shape & expression
    const eyeRyMap = { round: 7, almond: 5, sleepy: 3 };
    let eyeRy = eyeRyMap[eyeShape] ?? 7;
    if (level === 3) eyeRy = Math.max(4, eyeRy - 2);
    let leftEye = (
        <ellipse cx="84" cy="84" rx="6" ry={eyeRy} fill={eyeColor} />
    );
    let rightEye = (
        <ellipse cx="116" cy="84" rx="6" ry={eyeRy} fill={eyeColor} />
    );
    if (eyeShape === "sleepy") {
        leftEye = <path d="M78 84 Q84 90 90 84" stroke={eyeColor} strokeWidth="3" fill="none" strokeLinecap="round" />;
        rightEye = <path d="M110 84 Q116 90 122 84" stroke={eyeColor} strokeWidth="3" fill="none" strokeLinecap="round" />;
    }
    if (expression === "sleeping") {
        leftEye = <path d="M76 86 Q84 80 92 86" stroke="#0a0e1a" strokeWidth="3" fill="none" strokeLinecap="round" />;
        rightEye = <path d="M108 86 Q116 80 124 86" stroke="#0a0e1a" strokeWidth="3" fill="none" strokeLinecap="round" />;
    }

    // Mouth path
    const mouths = {
        smile: <path d="M92 104 Q100 112 108 104" stroke="#0a0e1a" strokeWidth="2" fill="none" strokeLinecap="round" />,
        grin: <path d="M88 102 Q100 116 112 102 Z" fill="#ff8fa3" stroke="#0a0e1a" strokeWidth="1.5" />,
        smirk: <path d="M92 106 Q104 112 110 102" stroke="#0a0e1a" strokeWidth="2" fill="none" strokeLinecap="round" />,
    };
    let mouthEl = mouths[mouthKind] || mouths.smile;
    if (expression === "sad" || expression === "tear" || expression === "hurt") {
        mouthEl = (
            <path d="M92 110 Q100 102 108 110" stroke="#0a0e1a" strokeWidth="2" fill="none" strokeLinecap="round" />
        );
    }
    if (expression === "sleeping") {
        mouthEl = <line x1="96" y1="106" x2="104" y2="106" stroke="#0a0e1a" strokeWidth="1.6" strokeLinecap="round" />;
    }

    // Tear / hurt overlays
    const tear = (expression === "tear" || expression === "hurt") && (
        <ellipse cx="86" cy="98" rx="3" ry="5" fill="#7cc1ff" opacity="0.85" />
    );
    const hurt = expression === "hurt" && (
        <>
            <line x1="76" y1="74" x2="92" y2="86" stroke="#ff6b6b" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="92" y1="74" x2="76" y2="86" stroke="#ff6b6b" strokeWidth="2.5" strokeLinecap="round" />
        </>
    );

    return (
        <div
            ref={containerRef}
            className={`relative inline-block ${animClass}`}
            style={{ width: size, height: size, opacity }}
            data-testid="blueberry-character"
            data-level={level}
        >
            {level === 4 && (
                <>
                    <span className="absolute left-1/2 top-1/2" style={{ animation: "bb-sparkle-orbit 6s linear infinite" }}>
                        <Sparkles className="text-yellow-300" size={22} />
                    </span>
                    <span className="absolute left-1/2 top-1/2" style={{ animation: "bb-sparkle-orbit 9s linear infinite reverse", animationDelay: "-2s" }}>
                        <Star className="text-[#7cc1ff]" size={18} fill="#7cc1ff" />
                    </span>
                </>
            )}

            <svg viewBox="0 0 200 200" width={size} height={size} xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <radialGradient id={gradId} cx="50%" cy="40%" r="65%">
                        <stop offset="0%" stopColor={lighten(fur, 60)} />
                        <stop offset="55%" stopColor={fur} />
                        <stop offset="100%" stopColor={lighten(fur, -50)} />
                    </radialGradient>
                </defs>
                <path d="M150 130 Q190 110 175 80 Q165 70 158 85" fill={fillFur} stroke={stroke} strokeWidth={strokeWidth} />
                <ellipse cx="100" cy="135" rx="55" ry="48" fill={fillFur} stroke={stroke} strokeWidth={strokeWidth} />
                {level !== 1 && <ellipse cx="100" cy="148" rx="28" ry="22" fill={lighten(fur, 80)} opacity="0.5" />}
                <circle cx="100" cy="85" r="48" fill={fillFur} stroke={stroke} strokeWidth={strokeWidth} />
                <path d="M62 55 L72 28 L86 58 Z" fill={fillFur} stroke={stroke} strokeWidth={strokeWidth} />
                <path d="M138 55 L128 28 L114 58 Z" fill={fillFur} stroke={stroke} strokeWidth={strokeWidth} />
                {level !== 1 && (
                    <>
                        <path d="M68 50 L73 36 L80 52 Z" fill="#ffb8d1" />
                        <path d="M132 50 L127 36 L120 52 Z" fill="#ffb8d1" />
                    </>
                )}

                {level >= 3 ? (
                    <>
                        {leftEye}
                        {rightEye}
                        {eyeShape !== "sleepy" && (
                            <>
                                <circle cx="86" cy="82" r="2" fill="white" />
                                <circle cx="118" cy="82" r="2" fill="white" />
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <path d="M78 86 Q84 80 90 86" stroke={eyeColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity={level === 1 ? 0.4 : 1} />
                        <path d="M110 86 Q116 80 122 86" stroke={eyeColor} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity={level === 1 ? 0.4 : 1} />
                    </>
                )}

                {level !== 1 && (
                    <>
                        <path d="M97 96 L100 100 L103 96 Z" fill="#ff8fa3" />
                        {mouthEl}
                        <line x1="70" y1="100" x2="86" y2="102" stroke="white" strokeWidth="1" opacity="0.8" />
                        <line x1="70" y1="106" x2="86" y2="105" stroke="white" strokeWidth="1" opacity="0.8" />
                        <line x1="114" y1="102" x2="130" y2="100" stroke="white" strokeWidth="1" opacity="0.8" />
                        <line x1="114" y1="105" x2="130" y2="106" stroke="white" strokeWidth="1" opacity="0.8" />
                    </>
                )}

                {tear}
                {hurt}

                {level !== 1 && (
                    <>
                        <ellipse cx="78" cy="178" rx="14" ry="8" fill={fillFur} />
                        <ellipse cx="122" cy="178" rx="14" ry="8" fill={fillFur} />
                    </>
                )}
            </svg>

            {Object.entries(equipped || {}).map(([slot, itemId]) => {
                if (!itemId) return null;
                const custom = positions?.[slot] || localPos?.[slot];
                const overridePos = custom
                    ? {
                          left: `${custom.x}%`,
                          top: `${custom.y}%`,
                          transform: "translate(-50%, -50%)",
                          right: "auto",
                          bottom: "auto",
                      }
                    : null;
                const isDragging = dragSlot === slot;
                const dragHandlers = draggable
                    ? {
                          onPointerDown: (e) => beginDrag(slot, e),
                          onPointerMove: (e) => onDragMove(slot, e),
                          onPointerUp: () => endDrag(slot),
                          onPointerCancel: () => endDrag(slot),
                      }
                    : {};
                const dragStyle = draggable
                    ? { cursor: isDragging ? "grabbing" : "grab", touchAction: "none" }
                    : {};
                const isCustom = typeof itemId === "string" && itemId.startsWith("cst_");
                if (isCustom) {
                    const cust = customMap[itemId];
                    if (!cust) return null;
                    const slotPos = {
                        head: { top: "-22%", left: "50%", transform: "translateX(-50%)", width: size * 0.45 },
                        neck: { top: "55%", left: "50%", transform: "translateX(-50%)", width: size * 0.32 },
                        back: { top: "20%", left: "-15%", width: size * 0.5 },
                        aura: { top: "-10%", right: "-10%", width: size * 0.3 },
                        companion: { bottom: "0%", right: "-25%", width: size * 0.3 },
                        mount: { bottom: "-15%", left: "50%", transform: "translateX(-50%)", width: size * 0.7 },
                    };
                    const pos = slotPos[slot] || slotPos.head;
                    const finalStyle = overridePos
                        ? { ...pos, ...overridePos, width: pos.width }
                        : pos;
                    return (
                        <img
                            key={slot}
                            src={cust.data_url}
                            alt={cust.name}
                            className={`absolute select-none ${draggable ? "" : "pointer-events-none"}`}
                            style={{
                                ...finalStyle,
                                ...dragStyle,
                                zIndex: slot === "back" || slot === "mount" ? 0 : 5,
                                filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.45))",
                            }}
                            draggable={false}
                            {...dragHandlers}
                            data-testid={`equipped-custom-${slot}`}
                        />
                    );
                }
                const v = ACCESSORY_VISUALS[itemId];
                if (!v) return null;
                const finalStyle = overridePos ? { ...v.style, ...overridePos } : v.style;
                return (
                    <span
                        key={slot}
                        className={`absolute select-none ${draggable ? "" : "pointer-events-none"}`}
                        style={{
                            ...finalStyle,
                            ...dragStyle,
                            zIndex: slot === "back" || slot === "mount" ? 0 : 5,
                            filter: v.style?.filter || "drop-shadow(0 4px 12px rgba(0,0,0,0.4))",
                        }}
                        {...dragHandlers}
                        data-testid={`equipped-${slot}`}
                    >
                        {v.emoji}
                    </span>
                );
            })}
        </div>
    );
};

export default Blueberry;
